/**
 * Skymedic UK — professional share-link session
 *
 * `?access=<token>` is compared to the SHA-256 of the theme setting (hash only
 * is in the page; the raw token is never printed). On match:
 *   1. First-party cookie (14 days, Secure, SameSite=Lax, Path=/)
 *   2. Cart attribute `skymedic_pro_share` = raw token (Liquid + discount function)
 *   3. `access` is stripped from the address bar
 *
 * A boolean cart attribute is intentionally not used: `/cart/update.js` is
 * public, so only the matching secret unlocks PRO price at catalog and checkout.
 */
(function (global) {
  'use strict';

  var COOKIE_NAME = 'skymedic_pro_share';
  var ATTR_KEY = 'skymedic_pro_share';
  var COOKIE_DAYS = 14;

  function hidePage() {
    document.documentElement.style.visibility = 'hidden';
  }

  function showPage() {
    document.documentElement.style.visibility = '';
  }

  function readCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    if (parts.length < 2) return '';
    return decodeURIComponent(parts.pop().split(';').shift() || '');
  }

  function writeCookie(name, value, days) {
    var maxAge = Math.round(days * 24 * 60 * 60);
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      '; Max-Age=' +
      maxAge +
      '; Path=/; SameSite=Lax' +
      secure;
  }

  function clearCookie(name) {
    document.cookie = name + '=; Max-Age=0; Path=/; SameSite=Lax';
  }

  function currentUrl() {
    return new URL(location.href);
  }

  function stripAccessParam() {
    var url = currentUrl();
    if (!url.searchParams.has('access')) return false;
    url.searchParams.delete('access');
    var next = url.pathname + url.search + url.hash;
    history.replaceState(null, '', next);
    return true;
  }

  function reloadWithoutAccess() {
    var url = currentUrl();
    url.searchParams.delete('access');
    location.replace(url.pathname + url.search + url.hash);
  }

  function sha256hex(str) {
    var bytes = new TextEncoder().encode(str);
    return crypto.subtle.digest('SHA-256', bytes).then(function (buf) {
      return Array.from(new Uint8Array(buf))
        .map(function (b) {
          return b.toString(16).padStart(2, '0');
        })
        .join('');
    });
  }

  function updateCartAttribute(cartUpdateUrl, token) {
    var attributes = {};
    attributes[ATTR_KEY] = token;
    return fetch(cartUpdateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes: attributes }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart update failed');
      return res.json();
    });
  }

  function init(cfg) {
    if (!cfg || !cfg.expectedHash || !cfg.cartUpdateUrl) return;
    if (typeof crypto === 'undefined' || !crypto.subtle) return;

    var param = '';
    try {
      param = currentUrl().searchParams.get('access') || '';
    } catch (e) {
      param = '';
    }
    var cookieToken = readCookie(COOKIE_NAME);
    var cartUnlocked = cfg.cartUnlocked === true;

    if (!param && !cookieToken) return;

    if (param || (cookieToken && !cartUnlocked)) {
      hidePage();
    }

    function finishVisible() {
      if (param) stripAccessParam();
      showPage();
    }

    function applyToken(token) {
      writeCookie(COOKIE_NAME, token, COOKIE_DAYS);
      if (cartUnlocked) {
        finishVisible();
        return Promise.resolve();
      }
      return updateCartAttribute(cfg.cartUpdateUrl, token).then(function () {
        reloadWithoutAccess();
      });
    }

    function consider(token) {
      if (!token) return Promise.resolve(false);
      return sha256hex(token).then(function (hash) {
        return hash === cfg.expectedHash;
      });
    }

    consider(param)
      .then(function (paramOk) {
        if (paramOk) return applyToken(param);
        if (param && cookieToken && param !== cookieToken) {
          return consider(cookieToken).then(function (cookieOk) {
            if (cookieOk) return applyToken(cookieToken);
            clearCookie(COOKIE_NAME);
            finishVisible();
          });
        }
        if (param) {
          finishVisible();
          return null;
        }
        return consider(cookieToken).then(function (cookieOk) {
          if (cookieOk) return applyToken(cookieToken);
          if (cookieToken) clearCookie(COOKIE_NAME);
          finishVisible();
        });
      })
      .catch(function () {
        finishVisible();
      });
  }

  global.SkymedicProShare = {
    init: init,
    cookieName: COOKIE_NAME,
    attributeKey: ATTR_KEY,
    cookieDays: COOKIE_DAYS,
  };
})(window);
